import { TokenList } from '../../types'

const PENDLE_API_BASE = 'https://api-v2.pendle.finance/core'
const MARKETS_ENDPOINT = `${PENDLE_API_BASE}/v1/markets/all`
const PRICES_ENDPOINT = (types: PendleAssetTypes[] | undefined) =>
  `${PENDLE_API_BASE}/v1/prices/assets${types ? `?type=${types.join(',')}` : ''}`

type PendleAssetTypes = 'PT' | 'YT' | 'SY' | 'PENDLE_LP'

interface PendleMarket {
  name: string
  address: string
  expiry: string
  pt: string
  yt: string
  sy: string
  underlyingAsset: string
  details: any
  isNew: boolean
  isPrime: boolean
  timestamp: string
  lpWrapper?: string
  categoryIds: string[]
  chainId: number
}

interface PendleMarketsResponse {
  markets: PendleMarket[]
}

interface PendlePricesResponse {
  prices: { [address: string]: number }
  total: number
  skip: number
  limit: number
}

interface PendleMarketsByChain {
  [chainId: string]: PendleMarket[]
}

export async function fetchPendleMarkets(): Promise<PendleMarketsByChain> {
  try {
    const response = await fetch(MARKETS_ENDPOINT)

    if (!response.ok) {
      throw new Error(`Failed to fetch all markets data: ${response.status}`)
    }

    const data: PendleMarketsResponse = await response.json()

    const marketsByChain: PendleMarketsByChain = {}

    if (data.markets) {
      for (const market of data.markets) {
        const chainId = market.chainId.toString()
        if (!marketsByChain[chainId]) {
          marketsByChain[chainId] = []
        }
        marketsByChain[chainId].push(market)
      }
    }

    return marketsByChain
  } catch (error) {
    console.error('Error fetching all markets data:', error)
    return {}
  }
}

/**
 * Fetch prices for a specific chain and token type
 */
async function fetchPendlePricesForAllChain(
  lists: { [chainId: string]: { list: TokenList } } = {},
  assetType: PendleAssetTypes[] = ['YT', 'PT'],
): Promise<{
  [address: string]: number
}> {
  try {
    const data = await fetchPendlePricesRaw(assetType)
    let prices: { [address: string]: number } = {}
    Object.entries(data).forEach(([key, val]) => {
      const [chainId, address] = key.split('-')
      const assetKey =
        lists[chainId]?.list?.[address.toLowerCase()]?.assetGroup ?? key
      prices[assetKey] = val
    })
    return prices
  } catch (error) {
    console.error(`Error fetching prices pendle prices:`, error)

    return {}
  }
}

/**
 * Fetch prices for a specific chain and token type
 */
async function fetchPendlePricesRaw(
  assetType: PendleAssetTypes[] = ['YT', 'PT'],
): Promise<{
  [address: string]: number
}> {
  try {
    const response = await fetch(PRICES_ENDPOINT(assetType))
    const data = (await response.json()) as PendlePricesResponse
    return data.prices
  } catch (error) {
    console.error(`Error fetching prices pendle prices:`, error)

    return {}
  }
}

/**
 * Main function to fetch Pendle prices
 */
export async function fetchPendlePrices(
  lists: { [chainId: string]: { list: TokenList } } = {},
  assetType: PendleAssetTypes[] = ['YT', 'PT'],
): Promise<{
  [address: string]: number
}> {
  try {
    const marketsResponse = await fetchPendleMarkets()

    if (!Object.keys(marketsResponse).length) {
      return {}
    }

    const allPrices = await fetchPendlePricesForAllChain(lists, assetType) // fetch YT and PT prices per default
    return Object.fromEntries(
      Object.entries(allPrices).filter(([, price]) => !!price),
    )
  } catch (error) {
    console.error('Error in fetchPendlePrices:', error)
    return {}
  }
}

/**
 * Main function to fetch Pendle yields
 * Provide lists to map to asset groups
 * For all chains
 * Skips 0 APRs
 */
export async function fetchPendleYields(
  lists: { [chainId: string]: { list: TokenList } } = {},
): Promise<{
  [address: string]: number
}> {
  try {
    const marketsResponse = await fetchPendleMarkets()

    if (!Object.keys(marketsResponse).length) {
      return {}
    }

    const prices = await fetchPendlePricesRaw() // YT and PT
    let yields: any = {}
    const now = new Date()
    Object.entries(marketsResponse).forEach(([chain, dataArr]) => {
      dataArr.forEach((market) => {
        const ptPrice = prices[market.pt]
        const ytPrice = prices[market.yt]

        const [chainId, address] = market.pt.split('-')
        // Only emit yields for PTs we actually track, keyed by their asset_group.
        // The old `?? market.pt` fallback wrote a raw `chainId-address` key that
        // never matched any market's asset_group — so when a PT was curated out
        // of the token list around expiry, its zero landed on that phantom key
        // while the real asset_group row kept its stale pre-expiry yield forever.
        // Skipping untracked PTs stops the junk keys; the yield-tracer side zeroes
        // already-orphaned rows via the market's own `meta.pendle.expiry`.
        const assetKey = lists[chainId]?.list?.[address.toLowerCase()]?.assetGroup
        if (!assetKey) return

        const expiry = new Date(market.expiry)
        if (expiry <= now) {
          yields[assetKey] = 0
          return
        }

        if (ytPrice && ytPrice > 0) {
          const yearFraction = yearFractionToExpiry(market.expiry, now)
          const apr = ((ytPrice / ptPrice) * 100) / yearFraction
          yields[assetKey] = apr
        }
      })
    })

    return yields
  } catch (error) {
    console.error('Error in fetchPendlePrices:', error)
    return {}
  }
}

function yearFractionToExpiry(expiryISO: string, now = new Date()): number {
  const expiry = new Date(expiryISO)

  // Difference in milliseconds
  const diffMs = expiry.getTime() - now.getTime()

  // Convert ms → days → years (approx 365.25 to account for leap years)
  const yearFraction = diffMs / (1000 * 60 * 60 * 24 * 365.25)

  return yearFraction
}

// // Example usage:
// const expiry = '2025-12-25T00:00:00.000Z';
// console.log(yearFractionToExpiry(expiry)); // e.g. 0.22 years
